import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API } from "../../constants/config";

const GD = "#8B6914";
const GDH = "#D4AF37";
const BG = "#FDFBF7";
const TXT = "#2D1810";
const TXT2 = "#6B4C3B";
const HERO_BG = "#1a2744";
const HERO_BG2 = "#0f1d35";
const HERO_GOLD = "#d4a84b";

const THEMES = {
  "red-gold":     { bg: "#6b1a1a", bg2: "#4a0f0f", gold: "#d4a84b" },
  "sakya":        { bg: "#1e3a5f", bg2: "#b91c1c", gold: "#f0c040" },
  "indigo-gold":  { bg: "#1a2744", bg2: "#0f1d35", gold: "#d4a84b" },
  "forest-gold":  { bg: "#1a2e1a", bg2: "#122212", gold: "#d4a84b" },
  "parchment-gold": { bg: "#E8DDC2", bg2: "#F5EFE0", gold: "#C8A76B", light: true },
};

function L(zh, en) {
  return (typeof window !== "undefined" && localStorage.getItem("lumina_lang") === "en") ? en : zh;
}

// ── Slide builder ──
function buildSlides(series, events, theme) {
  const slides = [];
  // Slide 1: series flyer
  if (series.flyer_url) {
    slides.push({
      image: series.flyer_url,
      title_zh: series.title_zh,
      title_en: series.title_en || series.title_zh,
      sub_zh: `${series.start_date || ""}${series.end_date && series.end_date !== series.start_date ? " – " + series.end_date : ""}`,
      sub_en: `${series.start_date || ""}${series.end_date && series.end_date !== series.start_date ? " – " + series.end_date : ""}`,
    });
  }
  // Slides 2+: event covers
  for (const ev of (events || [])) {
    const img = ev.cover_image_url || ev.flyer_url;
    if (img && img !== series.flyer_url) {
      slides.push({
        image: img,
        title_zh: ev.title_zh || "",
        title_en: ev.title_en || "",
        sub_zh: `${ev.start_date || ""} · ${ev.location || ""}`,
        sub_en: `${ev.start_date || ""} · ${ev.location || ""}`,
      });
    }
  }
  // Fallback gradient slide
  if (slides.length === 0) {
    slides.push({
      grad: `linear-gradient(135deg, ${theme.bg}, ${theme.bg2})`,
      title_zh: series.title_zh,
      title_en: series.title_en || series.title_zh,
      sub_zh: series.start_date || "",
      sub_en: series.start_date || "",
    });
  }
  return slides;
}

// ── Session parser ──
function parseSessions(ev) {
  let s = ev.sessions;
  if (typeof s === "string") { try { s = JSON.parse(s); } catch { s = null; } }
  return Array.isArray(s) ? s : [];
}

// ── Suggested donation amount (CAD) ──
function extractAmountCad(ev) {
  // Structured fee (cents) wins; otherwise parse "$NNN" from price text
  if (ev && ev.fee_amount) return Math.round(ev.fee_amount) / 100;
  const txt = `${(ev && ev.price_info_zh) || ""} ${(ev && ev.price_info_en) || ""}`;
  const m = txt.match(/\$\s*([\d]+(?:\.[\d]+)?)/);
  return m ? parseFloat(m[1]) : 0;
}


export default function SpecialSeriesPage({ lang }) {
  const { slug } = useParams();
  const navigate = useNavigate();
  const registerRef = useRef(null);
  const isEn = lang === "en";

  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);

  // Registration form
  const [form, setForm] = useState({ name:"", email:"", phone:"", wechat:"", dharma_name:"", notes:"" });
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [regError, setRegError] = useState("");
  const updateF = (k,v) => setForm(f=>({...f,[k]:v}));

  // ── Inline subscribe ──
  const [subEmail, setSubEmail] = useState("");
  const [subStatus, setSubStatus] = useState("idle");
  const [subMsg, setSubMsg] = useState("");
  // ── Lineage detection from series data ──
  const lineageLabel = (() => {
    const txt = (series?.description_zh||"") + (series?.description_en||"") + (series?.title_zh||"") + (series?.teacher_zh||"");
    const lo = txt.toLowerCase();
    if (lo.includes("噶举") || lo.includes("kagyü") || lo.includes("kagyu") || lo.includes("drikung")) return "噶举";
    if (lo.includes("宁玛") || lo.includes("nyingma")) return "宁玛";
    if (lo.includes("格鲁") || lo.includes("gelug")) return "格鲁";
    if (lo.includes("萨迦") || lo.includes("sakya")) return "萨迦";
    if (lo.includes("觉囊") || lo.includes("jonang")) return "觉囊";
    return "General";
  })();
  const lineageLabelEn = lineageLabel === "General" ? "General" : lineageLabel;
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subEmail.trim()) return;
    setSubStatus("loading"); setSubMsg("");
    try {
      const r = await fetch("/api/subscribe", {
        method: "POST", headers: {"Content-Type": "application/json"},
        body: JSON.stringify({ email: subEmail.trim(), labels: ["General", lineageLabel] }),
      });
      const d = await r.json();
      if (r.ok) setSubStatus("success");
      else { setSubStatus("error"); setSubMsg(d.detail || L("订阅失败","Failed")); }
    } catch {
      setSubStatus("error"); setSubMsg(L("网络错误","Network error"));
    }
  };

  useEffect(() => {
    if (!slug) return;
    fetch(`${API}/vajra/special/${slug}`)
      .then(r => r.ok ? r.json() : null)
      .then(d => { setSeries(d); if (d?.events) setSelectedEvents(d.events.map(e=>e.id)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <div style={{ minHeight:"100vh", background:BG }} />;
  if (!series) return (
    <div style={{ minHeight:"100vh", background:BG, display:"flex", alignItems:"center", justifyContent:"center", flexDirection:"column", gap:16 }}>
      <div style={{ fontSize:40 }}>🙏</div>
      <div style={{ fontSize:16, color:TXT2 }}>{L("页面未找到","Page not found")}</div>
      <button onClick={() => navigate("/vajra")} style={{ padding:"8px 24px", borderRadius:8, border:`1px solid ${GD}`, background:"transparent", color:GD, cursor:"pointer", fontSize:13 }}>{L("返回首页","Back")}</button>
    </div>
  );

  const events = Array.isArray(series.events) ? series.events : [];
  const theme = THEMES[series.theme_color] || THEMES["indigo-gold"];
  const isLight = !!theme.light;
  const btnText = isLight ? "#3a2d1a" : theme.bg;
  const slides = buildSlides(series, events, theme);
  const isPast = series.end_date ? new Date() > new Date(series.end_date + "T23:59:59-04:00") : false;

  const teacherBio = isEn ? (series.teacher_bio_en || "") : (series.teacher_bio_zh || "");
  const payInfo = isEn ? (series.payment_info_en || series.payment_info_zh || "") : (series.payment_info_zh || series.payment_info_en || "");
  const organizer = isEn ? (series.organizer_en || series.organizer_zh || "") : (series.organizer_zh || series.organizer_en || "");

  // Suggested donation total for currently-selected events (CAD)
  const selectedTotal = events
    .filter(ev => selectedEvents.includes(ev.id))
    .reduce((sum, ev) => sum + extractAmountCad(ev), 0);
  const ppAmount = selectedTotal > 0 ? `&amount=${selectedTotal}` : "";

  const handleRegister = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) { setRegError(L("请填写姓名和邮箱","Please fill in name and email")); return; }
    setSubmitting(true); setRegError("");
    const payload = { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim()||undefined, wechat: form.wechat.trim()||undefined, dharma_name: form.dharma_name.trim()||undefined, notes: form.notes.trim()||undefined };
    try {
      for (const ev of events) {
        if (!selectedEvents.includes(ev.id)) continue;
        await fetch(`${API}/vajra/events/${ev.id}/register`, { method:"POST", headers:{"Content-Type":"application/json"}, body: JSON.stringify(payload) });
      }
      setSubmitted(true);
    } catch { setRegError(L("报名失败","Registration failed")); }
    setSubmitting(false);
  };

  return (
    <div style={{ minHeight:"100vh", background: isLight ? "#F5EFE0" : BG, fontFamily:"'Noto Sans SC','Source Sans Pro',sans-serif" }}>

      {/* Nav */}
      <div style={{ position:"sticky",top:0,zIndex:100,background:"rgba(253,251,247,.95)",backdropFilter:"blur(12px)",borderBottom:"1px solid rgba(139,105,20,.1)",padding:"10px 24px",display:"flex",alignItems:"center",gap:24 }}>
        <span onClick={()=>navigate("/vajra")} style={{ fontSize:15,fontWeight:600,color:GD,cursor:"pointer",letterSpacing:1 }}>☸ 如意宝金刚社群</span>
        <div style={{ display:"flex",gap:16,fontSize:12,color:TXT2,flexWrap:"wrap" }}>
          {[[L("首页","Home"),"/vajra"],[L("修行","Practice"),"/vajra/practice"],[L("法讯","Events"),"/vajra/events"],[L("传承上师","Teachers"),"/vajra/teachers"],[L("服务","Service"),"/vajra/service"],[L("法教","Teachings"),"/vajra/teachings"],[L("订阅法讯","Subscribe"),"/vajra/subscribe"]].map(([l,p])=>(
            <span key={p} onClick={()=>navigate(p)} style={{ cursor:"pointer",opacity:.7 }}>{l}</span>
          ))}
        </div>
      </div>

      {/* Sliding Banner */}
      {slides.length > 1 ? (
        <div style={{ position:"relative",width:"100%",height:"min(50vw,360px)",overflow:"hidden",background:theme.bg }}>
          {slides[currentSlide].image ? (
            <img src={slides[currentSlide].image} alt="" style={{ width:"100%",height:"100%",objectFit:"cover",objectPosition:"top center",opacity:.55 }} />
          ) : <div style={{ width:"100%",height:"100%",background:slides[currentSlide].grad }} />}
          <div style={{ position:"absolute",inset:0,background:`linear-gradient(to top,${theme.bg2}ee 0%,${theme.bg2}4d 50%,${theme.bg2}80 100%)` }} />
          <div style={{ position:"absolute",bottom:40,left:0,right:0,padding:"0 24px",maxWidth:760,margin:"0 auto" }}>
            <h2 style={{ fontSize:"clamp(18px,3.5vw,26px)",fontWeight:700,color:"#fff",margin:"0 0 6px",textShadow:"0 2px 12px rgba(0,0,0,.4)" }}>{isEn ? slides[currentSlide].title_en : slides[currentSlide].title_zh}</h2>
            <p style={{ fontSize:14,color:"rgba(255,255,255,.6)",margin:0,textShadow:"0 1px 6px rgba(0,0,0,.3)" }}>{isEn ? slides[currentSlide].sub_en : slides[currentSlide].sub_zh}</p>
          </div>
          <div style={{ position:"absolute",bottom:12,left:0,right:0,display:"flex",justifyContent:"center",gap:6 }}>
            {slides.map((_,i)=>(<button key={i} onClick={()=>setCurrentSlide(i)} style={{ width:i===currentSlide?18:7,height:7,borderRadius:4,border:"none",cursor:"pointer",background:i===currentSlide?theme.gold:"rgba(255,255,255,.3)",transition:"all .3s" }} />))}
          </div>
          {slides.length>1&&<><button onClick={()=>setCurrentSlide(c=>(c-1+slides.length)%slides.length)} style={{ position:"absolute",top:"50%",left:8,transform:"translateY(-50%)",width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,.1)",border:"none",color:"#fff",fontSize:20,cursor:"pointer" }}>‹</button>
          <button onClick={()=>setCurrentSlide(c=>(c+1)%slides.length)} style={{ position:"absolute",top:"50%",right:8,transform:"translateY(-50%)",width:36,height:36,borderRadius:"50%",background:"rgba(255,255,255,.1)",border:"none",color:"#fff",fontSize:20,cursor:"pointer" }}>›</button></>}
        </div>
      ) : isLight ? (
        /* Light parchment hero — flyer portrait + text */
        <div style={{ background:"linear-gradient(160deg,#F7F1E3 0%,#EEE2C9 55%,#E4D4B4 100%)",color:TXT,padding:"40px 24px 48px",position:"relative",overflow:"hidden" }}>
          <div style={{ position:"absolute",inset:0,opacity:.5,background:"radial-gradient(circle at 15% 15%, rgba(200,167,107,.22) 0%, transparent 50%),radial-gradient(circle at 85% 75%, rgba(200,167,107,.16) 0%, transparent 50%)" }} />
          <div style={{ maxWidth:900,margin:"0 auto",position:"relative",zIndex:1,display:"flex",gap:36,alignItems:"center",justifyContent:"center",flexWrap:"wrap" }}>
            {slides[0]?.image&&<img src={slides[0].image} alt={series.title_zh} style={{ width:"100%",maxWidth:280,borderRadius:12,boxShadow:"0 12px 40px rgba(47,47,47,.25)",border:"2px solid #C8A76B" }} />}
            <div style={{ flex:1,minWidth:280 }}>
              {isPast&&<div style={{ fontSize:13,color:"#8B2D2D",marginBottom:16,padding:"8px 16px",borderRadius:8,background:"rgba(139,45,45,.08)",border:"1px solid rgba(139,45,45,.2)" }}>{L("此法会已圆满","This event has concluded")}</div>}
              <div style={{ fontSize:12,letterSpacing:3,color:"#8B6914",fontWeight:700,textTransform:"uppercase",marginBottom:10 }}>{L("首次莅临加拿大","First Teaching Visit to Canada")}</div>
              <h1 style={{ fontSize:"clamp(24px,4vw,34px)",fontWeight:700,lineHeight:1.3,margin:"0 0 6px",color:TXT,fontFamily:"Georgia,'Noto Serif SC','Songti SC',serif" }}>{isEn&&series.title_en?series.title_en:series.title_zh}</h1>
              {series.teacher_zh&&<div style={{ fontSize:18,color:"#8B2D2D",fontWeight:600,margin:"0 0 10px" }}>{isEn&&series.teacher_en?series.teacher_en:series.teacher_zh}</div>}
              <div style={{ display:"flex",gap:16,flexWrap:"wrap",fontSize:14,color:TXT2,marginBottom:8 }}>
                <span>📅 {series.start_date||L("日期待定","TBD")}{series.end_date&&series.end_date!==series.start_date?` – ${series.end_date}`:""}</span>
                <span>📍 Toronto</span>
              </div>
              {(series.description_zh||series.description_en||series.description)&&<p style={{ fontSize:14,color:TXT2,lineHeight:1.7,margin:"0 0 16px" }}>{isEn?(series.description_en||series.description_zh||series.description):(series.description_zh||series.description)}</p>}
              {!isPast&&<button onClick={()=>registerRef.current?.scrollIntoView({behavior:"smooth"})} style={{ padding:"14px 40px",fontSize:15,fontWeight:600,background:"linear-gradient(135deg,#C8A76B,#a8863f)",color:"#fff",border:"none",borderRadius:10,cursor:"pointer",boxShadow:"0 4px 20px rgba(200,167,107,.4)" }}>{L("立即报名 →","Register Now →")}</button>}
            </div>
          </div>
        </div>
      ) : (
        /* Single slide: static hero */
        <div style={{ background:`radial-gradient(ellipse 600px 300px at 50% 5%, rgba(212,168,75,0.15) 0%, transparent 55%),radial-gradient(ellipse 400px 200px at 70% 15%, rgba(245,240,220,0.10) 0%, transparent 50%),linear-gradient(160deg,${theme.bg},${theme.bg2},${theme.bg})`,color:"#fff",padding:"40px 24px 48px",position:"relative",overflow:"hidden" }}>
          <div style={{ position:"absolute",inset:0,opacity:.04,background:`radial-gradient(circle at 30% 40%,${theme.gold} 0%,transparent 50%),radial-gradient(circle at 70% 60%,${theme.gold} 0%,transparent 50%)` }} />
          <div style={{ maxWidth:760,margin:"0 auto",position:"relative",zIndex:1 }}>
            {isPast&&<div style={{ fontSize:13,color:theme.gold,marginBottom:16,padding:"8px 16px",borderRadius:8,background:"rgba(212,168,75,.1)",border:"1px solid rgba(212,168,75,.2)" }}>{L("此法会已圆满","This event has concluded")}</div>}
            <h1 style={{ fontSize:"clamp(22px,4vw,32px)",fontWeight:700,lineHeight:1.35,margin:"0 0 12px" }}>{isEn&&series.title_en?series.title_en:series.title_zh}</h1>
            {(series.description_zh||series.description_en||series.description)&&<p style={{ fontSize:15,color:"rgba(255,255,255,.65)",lineHeight:1.7,margin:"0 0 8px" }}>{isEn?(series.description_en||series.description_zh||series.description):(series.description_zh||series.description)}</p>}
            <div style={{ display:"flex",gap:20,flexWrap:"wrap",fontSize:14,color:"rgba(255,255,255,.5)" }}>
              <span>📅 {series.start_date||L("日期待定","TBD")}{series.end_date&&series.end_date!==series.start_date?` – ${series.end_date}`:""}</span>
              {series.teacher_zh&&<span>👤 {series.teacher_zh}</span>}
            </div>
            {!isPast&&<button onClick={()=>registerRef.current?.scrollIntoView({behavior:"smooth"})} style={{ marginTop:20,padding:"14px 40px",fontSize:15,fontWeight:600,background:`linear-gradient(135deg,${theme.gold},#c49a3c)`,color:theme.bg,border:"none",borderRadius:10,cursor:"pointer",boxShadow:"0 4px 20px rgba(212,168,75,.35)" }}>{L("立即报名 →","Register Now →")}</button>}
          </div>
        </div>
      )}

      {/* Body */}
      <div style={{ maxWidth:760,margin:"0 auto",padding:"40px 24px 64px" }}>

        {/* Teacher */}
        {(teacherBio||series.teacher_photo_url)&&<>
          <STitle icon="👤" title={L("引导上师","Spiritual Guide")} />
          <div style={{ display:"flex",gap:20,marginBottom:40,flexWrap:"wrap" }}>
            {series.teacher_photo_url&&<img src={series.teacher_photo_url} alt="" style={{ width:"100%",maxWidth:240,borderRadius:12,objectFit:"contain",objectPosition:"top",flexShrink:0,boxShadow:"0 4px 16px rgba(0,0,0,.1)" }} />}
            <div style={{ flex:1,minWidth:200 }}>
              {series.teacher_zh&&<div style={{ fontSize:16,fontWeight:600,color:TXT,marginBottom:6 }}>{series.teacher_zh}</div>}
              {teacherBio&&<p style={{ fontSize:13,color:TXT2,lineHeight:1.8,margin:0 }}>{teacherBio}</p>}
            </div>
          </div>
        </>}

        {/* Events as schedule tables */}
        {events.map((ev,i)=>(
          <div key={ev.id} style={{ marginBottom:i<events.length-1?32:40 }}>
            <STitle icon={events.length>1?`${i+1}️⃣`:"📅"} title={isEn&&ev.title_en?ev.title_en:ev.title_zh} />
            <div style={{ display:"flex",flexWrap:"wrap",gap:14,marginBottom:16,fontSize:13,padding:"10px 14px",background:"rgba(139,105,20,.03)",borderRadius:10,border:"1px solid rgba(139,105,20,.06)" }}>
              {ev.start_date&&<span style={{ color:TXT,fontWeight:600 }}>📅 {ev.start_date}{ev.start_time?` ${ev.start_time}`:""}{ev.end_time&&ev.end_time!==ev.start_time?`–${ev.end_time}`:""}</span>}
              {ev.location&&<span style={{ color:TXT2 }}>📍 {ev.location}</span>}
              {ev.price_info_zh&&<span style={{ color:GD,fontWeight:500 }}>💰 {isEn?(ev.price_info_en||ev.price_info_zh):ev.price_info_zh}</span>}
            </div>
            <SessionList sessions={parseSessions(ev)} isEn={isEn} />
            {ev.subtitle_zh&&<div style={{ fontSize:11,color:TXT2,marginTop:8,paddingLeft:10,borderLeft:`2px solid ${GD}` }}>{isEn?(ev.subtitle_en||ev.subtitle_zh):ev.subtitle_zh}</div>}
          </div>
        ))}

        {/* Registration Form */}
        {!isPast&&<div ref={registerRef} style={{ marginTop:24 }}>
          <STitle icon="📝" title={L("在线报名","Register")} />
          <div style={{ background:"#fff",borderRadius:14,padding:"24px",border:"1.5px solid rgba(139,105,20,.12)",boxShadow:"0 4px 24px rgba(0,0,0,.04)" }}>
            {submitted?(
              <div style={{ textAlign:"center",padding:"32px 16px" }}>
                <div style={{ fontSize:48,marginBottom:12 }}>✅</div>
                <div style={{ fontSize:18,fontWeight:600,color:TXT,marginBottom:8 }}>{L("报名成功！","Registered!")}</div>
                <div style={{ fontSize:13,color:TXT2,lineHeight:2 }}>
                  {L("⚠️ 报名以收到功德金为确认。请通过以下方式缴纳：","⚠️ Registration confirmed upon receipt. Please send donation via:")}<br/>
                  {L("• E-transfer / PayPal: canada.btf@gmail.com","• E-transfer / PayPal: canada.btf@gmail.com")}<br/>
                  {L("• 或现场现金","• or cash on-site")}<br/>
                  {L("付款后我们将发送确认邮件","Confirmation email will be sent after payment")}
                  {payInfo.split('\n').map((line,i)=><span key={i}>{line}<br/></span>)}
                </div>
                {payInfo.includes("PayPal")&&<div style={{ marginTop:12 }}>
                  <a href={`https://www.paypal.com/cgi-bin/webscr?cmd=_xclick&business=canada.btf@gmail.com${ppAmount}&currency_code=CAD&item_name=${encodeURIComponent(series.title_zh||"Retreat")}`} target="_blank" rel="noopener" style={{ display:"inline-block",padding:"12px 28px",fontSize:14,fontWeight:600,borderRadius:8,background:"linear-gradient(135deg,#0070ba,#003087)",color:"#fff",textDecoration:"none" }}>₿ {L("PayPal 支付","Pay with PayPal")}</a>
                </div>}
                <button onClick={()=>{setSubmitted(false);setForm({name:"",email:"",phone:"",wechat:"",dharma_name:"",notes:""});setSelectedEvents(events.map(e=>e.id))}} style={{ marginTop:16,padding:"8px 24px",borderRadius:8,border:`1px solid ${GD}`,background:"transparent",color:GD,cursor:"pointer",fontSize:13 }}>{L("再报一人","Register Another")}</button>
              </div>
            ):(
              <form onSubmit={handleRegister} style={{ display:"flex",flexDirection:"column",gap:12 }}>
                {/* Event selection — show when multiple events */}
                {events.length > 1 && (
                  <div style={{ display:"flex",gap:14,flexWrap:"wrap",marginBottom:4 }}>
                    {events.map(ev => (
                      <label key={ev.id} style={{ display:"flex",alignItems:"center",gap:6,fontSize:13,color:TXT,cursor:"pointer" }}>
                        <input type="checkbox" checked={selectedEvents.includes(ev.id)}
                          onChange={e => setSelectedEvents(prev => e.target.checked ? [...prev, ev.id] : prev.filter(id => id !== ev.id))}
                          style={{ width:16,height:16,accentColor:GD }} />
                        {isEn && ev.title_en ? ev.title_en : ev.title_zh}
                      </label>
                    ))}
                  </div>
                )}
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <InputF label={L("姓名 *","Name *")} v={form.name} onChange={v=>updateF("name",v)} />
                  <InputF label={L("邮箱 *","Email *")} v={form.email} onChange={v=>updateF("email",v)} type="email" />
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <InputF label={L("电话","Phone")} v={form.phone} onChange={v=>updateF("phone",v)} />
                  <InputF label={L("微信","WeChat")} v={form.wechat} onChange={v=>updateF("wechat",v)} />
                </div>
                <InputF label={L("法名（如有）","Dharma Name")} v={form.dharma_name} onChange={v=>updateF("dharma_name",v)} />
                <InputF label={L("备注","Notes")} v={form.notes} onChange={v=>updateF("notes",v)} />
                {regError&&<div style={{ color:"#b91c1c",fontSize:13,padding:"8px 12px",borderRadius:8,background:"rgba(185,28,28,.05)" }}>{regError}</div>}
                <button type="submit" disabled={submitting} style={{ padding:"14px 32px",fontSize:15,fontWeight:600,background:submitting?"rgba(139,105,20,.3)":`linear-gradient(135deg,${theme.gold},#c49a3c)`,color:submitting?"rgba(255,255,255,.5)":btnText,border:"none",borderRadius:10,cursor:submitting?"wait":"pointer",boxShadow:"0 4px 16px rgba(212,168,75,.25)",alignSelf:"flex-start" }}>{submitting?L("提交中...","Submitting..."):L("提交报名 →","Submit →")}</button>
                <div style={{ fontSize:12,color:"#c44",marginTop:8,padding:"10px 14px",borderRadius:8,background:"rgba(204,68,68,0.06)",border:"1px solid rgba(204,68,68,0.12)" }}>{L("⚠️ 报名以收到功德金为确认，未付款视为未报名。","⚠️ Registration is confirmed only upon receipt of donation. Unpaid registrations are not held.")}</div>
                {payInfo&&<div style={{ fontSize:11,color:TXT2,marginTop:4,whiteSpace:"pre-line",lineHeight:1.7 }}>{payInfo}</div>}
              </form>
            )}
          </div>
        </div>}

        {/* ── Subscribe CTA ── */}
        <div style={{ marginTop: 32 }}>
          <div style={{
            background: "#fff", borderRadius: 14, padding: "24px 24px",
            border: `1.5px solid ${theme.gold}`,
            boxShadow: "0 4px 24px rgba(0,0,0,0.04)",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
              <span style={{ fontSize: 20 }}>📿</span>
              <div>
                <div style={{ fontSize: 15, fontWeight: 600, color: TXT }}>
                  {L(`订阅${lineageLabel}法讯`, `Subscribe to ${lineageLabelEn} Dharma News`)}
                </div>
                <div style={{ fontSize: 11, color: TXT2, marginTop: 2 }}>
                  {L(`接收${lineageLabel}传承法会、灌顶通知 · 可随时退订`, `${lineageLabelEn} lineage events & empowerments · unsubscribe anytime`)}
                </div>
              </div>
            </div>
            {subStatus === "success" ? (
              <div style={{ textAlign: "center", padding: "16px 0 4px", fontSize: 13, color: TXT2, lineHeight: 1.8 }}>
                <span style={{ fontSize: 28 }}>✅</span><br/>
                {L(`感谢订阅！${subEmail} 已加入${lineageLabel}法讯列表。`, `Thank you! ${subEmail} added to ${lineageLabelEn} newsletter.`)}
                <div style={{ marginTop: 8 }}>
                  <button onClick={() => { setSubStatus("idle"); setSubEmail(""); }}
                    style={{
                      padding: "4px 16px", borderRadius: 6,
                      border: `1px solid ${GD}`, background: "transparent",
                      color: GD, cursor: "pointer", fontSize: 11,
                    }}>
                    {L("订阅其他邮箱", "Subscribe another")}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubscribe} style={{ display: "flex", gap: 8, marginTop: 12, flexWrap: "wrap" }}>
                <input type="email" required value={subEmail} onChange={e => setSubEmail(e.target.value)}
                  placeholder={L("your@email.com", "your@email.com")}
                  style={{
                    flex: "1 1 200px", padding: "10px 14px", borderRadius: 8,
                    border: "1px solid rgba(139,105,20,0.2)", fontSize: 13, color: TXT,
                    background: BG, outline: "none", fontFamily: "inherit",
                  }}
                  onFocus={e => e.target.style.borderColor = theme.gold}
                  onBlur={e => e.target.style.borderColor = "rgba(139,105,20,0.2)"} />
                <button type="submit" disabled={subStatus === "loading"}
                  style={{
                    padding: "10px 22px", fontSize: 13, fontWeight: 600,
                    background: subStatus === "loading" ? "#ccc" : `linear-gradient(135deg,${theme.gold},#c49a3c)`,
                    color: subStatus === "loading" ? "#999" : btnText,
                    border: "none", borderRadius: 8, cursor: subStatus === "loading" ? "not-allowed" : "pointer",
                    fontFamily: "inherit",
                  }}>
                  {subStatus === "loading" ? L("提交中...","...") : L("订阅","Subscribe")}
                </button>
              </form>
            )}
            {subMsg && (
              <div style={{ marginTop: 8, fontSize: 12, color: subStatus === "error" ? "#C62828" : "#2E7D32", textAlign: "center" }}>
                {subMsg}
              </div>
            )}
            <div style={{ fontSize: 10, color: TXT2, marginTop: 8, textAlign: "center" }}>
              {L(`已自动选择「${lineageLabel}」传承标签`,`${lineageLabelEn} tradition label auto-selected`)}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{ textAlign:"center",marginTop:48,padding:"32px 24px",borderTop:"1px solid rgba(139,105,20,.08)",fontSize:12,color:TXT2 }}>
          {organizer&&<div style={{ fontWeight:600,color:GD,marginBottom:4 }}>{organizer}</div>}
          {series.contact&&<div>📧 {series.contact}</div>}
          {series.website&&<a href={series.website} target="_blank" rel="noopener" style={{ color:GD,fontSize:12 }}>{series.website.replace(/^https?:\/\//,'')}</a>}
        </div>
      </div>
    </div>
  );
}

// ── Helpers ──
function STitle({ icon, title }) {
  return <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:16 }}>
    <span style={{ fontSize:18 }}>{icon}</span>
    <h2 style={{ fontSize:17,fontWeight:600,color:TXT,margin:0 }}>{title}</h2>
    <div style={{ flex:1,height:1,background:"rgba(139,105,20,.1)",marginLeft:8 }} />
  </div>;
}

function SessionList({ sessions, isEn }) {
  if (!sessions.length) return null;
  return <div style={{ marginBottom:8 }}>{sessions.map((s,i)=>(
    <div key={i} style={{ display:"flex",gap:10,alignItems:"center",padding:"10px 0",borderBottom:i<sessions.length-1?"1px solid rgba(139,105,20,.05)":"none" }}>
      <div style={{ minWidth:64,fontSize:11,fontWeight:600,color:TXT2,background:"rgba(139,105,20,.04)",padding:"4px 8px",borderRadius:6,textAlign:"center" }}>{s.date||""}</div>
      <div style={{ minWidth:130,fontSize:11,color:TXT2,fontFamily:"monospace" }}>{s.time||""}</div>
      <div style={{ flex:1,fontSize:13,color:TXT,fontWeight:500 }}>{isEn?(s.title_en||s.title_zh):(s.title_zh||s.title_en)}</div>
    </div>
  ))}</div>;
}

function InputF({ label, v, onChange, type, placeholder }) {
  return <label style={{ display:"flex",flexDirection:"column",gap:4,fontSize:12,color:TXT2 }}>
    {label}
    <input type={type||"text"} value={v} onChange={e=>onChange(e.target.value)} placeholder={placeholder}
      style={{ padding:"10px 14px",borderRadius:8,border:"1px solid rgba(139,105,20,.18)",fontSize:14,color:TXT,background:BG,outline:"none" }}
      onFocus={e=>e.target.style.borderColor=GD} onBlur={e=>e.target.style.borderColor="rgba(139,105,20,.18)"} />
  </label>;
}
